<template>
  <div class="recharge-page page">
    <van-nav-bar title="充值" class="nav-bar">
      <template #left>
        <van-icon name="arrow-left" color="#fff" @click="back()" />
      </template>
      <template #right>
        <button type="button" class="record-link" @click="openRechargeRecords">充值记录</button>
      </template>
    </van-nav-bar>

    <div class="recharge-scroll">
      <div class="section">
        <div class="section-title">充值金额</div>
        <van-field
          v-model="money"
          class="money-field"
          type="number"
          input-align="right"
          :placeholder="amountPlaceholder"
        >
          <template #label>
            <span>金额</span>
          </template>
        </van-field>
        <div class="limit-row">
          <span>最低 {{ currentMethod.min }}</span>
          <span>最高 {{ currentMethod.max }}</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">充值方式</div>
        <div ref="methodSelect" class="custom-select" :class="{ open: showMethodPicker }">
          <button type="button" class="select-trigger" @click="showMethodPicker = !showMethodPicker">
            <div class="select-current">
              <div class="select-current-icon">
                <van-icon :name="currentMethod.icon || 'peer-pay'" />
              </div>
              <div class="select-current-text">
                <strong>{{ currentMethod.title || currentMethod.label || '请选择充值方式' }}</strong>
                <span>{{ currentMethod.min }} - {{ currentMethod.max }}</span>
              </div>
            </div>
            <div class="select-right">
              <span>切换</span>
              <van-icon name="arrow-down" />
            </div>
          </button>
          <div v-if="showMethodPicker" class="select-options">
            <button
              v-for="item in paymentMethods"
              :key="item.value"
              type="button"
              class="select-option"
              :class="{ active: item.value === payWay }"
              @click="confirmMethod(item)"
            >
              <div class="option-icon">
                <van-icon :name="item.icon" />
              </div>
              <div class="option-main">
                <strong>{{ item.title || item.label }}</strong>
                <span>{{ item.label }} · {{ item.min }} - {{ item.max }}</span>
              </div>
              <van-icon v-if="item.value === payWay" class="option-check" name="success" />
            </button>
          </div>
        </div>
      </div>

      <div class="section pay-info">
        <div class="section-title">{{ currentMethod.label }}信息</div>

        <template v-if="currentMethod.type === 'bank'">
          <div class="info-row">
            <span>收款银行</span>
            <strong>{{ currentMethod.bankName }}</strong>
          </div>
          <div class="info-row">
            <span>银行卡号</span>
            <strong>{{ currentMethod.bankCard }}</strong>
            <button type="button" class="copy-btn" @click="copyText(currentMethod.bankCard)">
              <van-icon name="description" />
              <span>复制</span>
            </button>
          </div>
          <div class="info-row">
            <span>收款姓名</span>
            <strong>{{ currentMethod.realName }}</strong>
            <button type="button" class="copy-btn" @click="copyText(currentMethod.realName)">
              <van-icon name="description" />
              <span>复制</span>
            </button>
          </div>
          <div class="info-tip">请按页面金额转账，完成后上传转账截图。</div>
        </template>

        <template v-else-if="currentMethod.type === 'usdt'">
          <div class="qr-box">
            <img :src="currentMethod.qr" alt="USDT收款二维码" />
          </div>
          <div class="address-box">
            <span>{{ currentMethod.address }}</span>
            <button type="button" class="copy-btn address-copy" @click="copyText(currentMethod.address)">
              <van-icon name="description" />
              <span>复制</span>
            </button>
          </div>
          <div class="usdt-summary">
            <div>
              <span>USDT 汇率</span>
              <strong>{{ currentMethod.usdtRate || '--' }}</strong>
            </div>
            <div>
              <span>应付 USDT</span>
              <strong>{{ usdtAmount }}</strong>
            </div>
          </div>
          <div class="info-tip">请按应付 USDT 数量完成转账，完成后上传转账截图。</div>
        </template>

        <template v-else>
          <div class="qr-box">
            <img :src="currentMethod.qr" :alt="currentMethod.label + '收款二维码'" />
          </div>
          <div class="info-tip">请使用{{ currentMethod.label }}扫码付款，完成后上传付款截图。</div>
        </template>
      </div>

      <div class="section">
        <div class="section-title">转账截图</div>
        <van-uploader
          v-model="proofFileList"
          :after-read="uploadProof"
          @delete="removeProof"
          :max-count="1"
          accept="image/*"
        >
          <div class="upload-box">
            <van-loading v-if="uploading" size="24px" color="#fff" />
            <template v-else>
              <van-icon name="photograph" />
              <span>{{ proofImage ? '已上传截图' : '上传转账截图' }}</span>
            </template>
          </div>
        </van-uploader>
      </div>

      <van-button
        class="submit-btn"
        type="info"
        block
        :loading="submitting"
        loading-text="提交中..."
        @click="submitRecharge"
      >
        提交充值
      </van-button>
    </div>

    <van-popup
      v-model="showRechargeRecords"
      round
      position="bottom"
      class="record-popup"
      :style="{ height: '72%' }"
    >
      <div class="record-panel">
        <div class="record-header">
          <strong>充值记录</strong>
          <van-icon name="cross" @click="showRechargeRecords = false" />
        </div>
        <van-loading v-if="recordLoading" class="record-loading" color="#e63fbc" />
        <van-empty v-else-if="rechargeRecords.length === 0" description="暂无充值记录" />
        <div v-else class="record-list">
          <div v-for="item in rechargeRecords" :key="item.id || item.order_no" class="record-item">
            <div class="record-item-top">
              <div>
                <strong>{{ item.money }}</strong>
                <span>{{ item.pay_title || getPayTitle(item.pay_way) }}</span>
              </div>
              <em :class="'record-status status-' + item.status">{{ getRecordStatusText(item) }}</em>
            </div>
            <div class="record-row">
              <span>订单号</span>
              <p>{{ item.order_no || '-' }}</p>
            </div>
            <div class="record-row">
              <span>提交时间</span>
              <p>{{ item.create_time || '-' }}</p>
            </div>
            <div class="record-row" v-if="item.desc">
              <span>说明</span>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
const defaultQr = require('../images/user/qrcoder.png');

export default {
  data() {
    return {
      balance: 0,
      money: '',
      payWay: '',
      proofImage: '',
      proofFileList: [],
      uploading: false,
      submitting: false,
      showMethodPicker: false,
      paymentMethods: [],
      showRechargeRecords: false,
      recordLoading: false,
      rechargeRecords: []
    };
  },
  computed: {
    currentMethod() {
      return this.paymentMethods.find(item => item.value === this.payWay) || this.paymentMethods[0] || {
        id: '',
        label: '充值',
        value: '',
        type: '',
        min: 0,
        max: 0,
        qr: defaultQr
      };
    },
    amountPlaceholder() {
      return '请输入' + this.currentMethod.min + '-' + this.currentMethod.max + '的充值金额';
    },
    usdtAmount() {
      const amount = Number(this.money);
      const rate = Number(this.currentMethod.usdtRate);
      if (!amount || !rate) {
        return '--';
      }
      return (amount / rate).toFixed(4);
    }
  },
  methods: {
    back() {
      this.$router.push({ path: '/Mine' });
    },
    selectMethod(value) {
      this.payWay = value;
      this.money = '';
    },
    confirmMethod(item) {
      this.selectMethod(item.value || item);
      this.showMethodPicker = false;
    },
    handleDocumentClick(event) {
      if (!this.showMethodPicker || !this.$refs.methodSelect) {
        return;
      }
      if (!this.$refs.methodSelect.contains(event.target)) {
        this.showMethodPicker = false;
      }
    },
    normalizeMethod(item) {
      const type = item.type || '';
      const label = item.type_name || item.title || this.getTypeName(type);

      return {
        raw: item,
        id: item.id,
        value: String(item.id),
        type: type,
        label: label,
        title: item.title || label,
        icon: this.getMethodIcon(type),
        min: Number(item.min_amount || 0),
        max: Number(item.max_amount || 0),
        qr: item.qrcode || item.logo || defaultQr,
        address: item.address || '',
        usdtRate: item.usdt_rate || '',
        bankName: item.bank_name || '',
        bankCard: item.bank_card || '',
        realName: item.real_name || '',
        remark: item.remark || ''
      };
    },
    getTypeName(type) {
      const names = {
        bank: '银行卡',
        wechat: '微信',
        wx: '微信',
        alipay: '支付宝',
        usdt: 'USDT'
      };
      return names[type] || '充值';
    },
    getMethodIcon(type) {
      const icons = {
        bank: 'card',
        wechat: 'wechat',
        wx: 'wechat',
        alipay: 'alipay',
        usdt: 'gold-coin-o'
      };
      return icons[type] || 'peer-pay';
    },
    getPayTitle(type) {
      return this.getTypeName(type);
    },
    getRecordStatusText(item) {
      const statusMap = {
        0: '未支付',
        1: '待审核',
        2: '已通过',
        3: '已拒绝'
      };
      return item.status_text || statusMap[Number(item.status)] || '未知状态';
    },
    openRechargeRecords() {
      this.showRechargeRecords = true;
      this.getRechargeRecords();
    },
    getRechargeRecords() {
      this.recordLoading = true;
      this.$http({
        method: 'get',
        url: 'user_get_recharge_list'
      }).then(res => {
        this.recordLoading = false;
        if (res.code === 200) {
          this.rechargeRecords = Array.isArray(res.data) ? res.data : [];
        } else {
          this.rechargeRecords = [];
          this.$toast(res.msg || '充值记录获取失败');
        }
      }).catch(() => {
        this.recordLoading = false;
        this.rechargeRecords = [];
        this.$toast.fail('充值记录获取失败');
      });
    },
    getRechargeConfig() {
      this.$http({
        method: 'get',
        url: 'recharge_config'
      }).then(res => {
        if (res.code === 200 && Array.isArray(res.data)) {
          this.paymentMethods = res.data
            .filter(item => Number(item.status) === 1)
            .sort((a, b) => Number(a.sort || 0) - Number(b.sort || 0))
            .map(this.normalizeMethod);

          if (this.paymentMethods.length) {
            this.payWay = this.paymentMethods[0].value;
          }
        } else {
          this.$toast(res.msg || '充值方式获取失败');
        }
      }).catch(() => {
        this.$toast.fail('充值方式获取失败');
      });
    },
    copyText(text) {
      if (!text) {
        this.$toast('暂无可复制内容');
        return;
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$toast('复制成功');
        }).catch(() => {
          this.copyByInput(text);
        });
        return;
      }

      this.copyByInput(text);
    },
    copyByInput(text) {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.$toast('复制成功');
    },
    formatImageUrl(url) {
      if (!url) {
        return '';
      }
      if (/^(https?:)?\/\//.test(url) || /^data:image\//.test(url) || /^blob:/.test(url)) {
        return url;
      }
      const path = url.charAt(0) === '/' ? url : '/' + url;
      return 'https://api.dpyzcoin.com' + path;
    },
    setProofImage(url, preview) {
      const previewUrl = preview || this.formatImageUrl(url);
      this.proofImage = url;
      this.proofFileList = previewUrl ? [{
        url: previewUrl,
        content: previewUrl,
        isImage: true
      }] : [];
    },
    uploadProof(file) {
      if (!file || !file.file) {
        this.$toast.fail('请选择截图');
        return;
      }

      const formData = new FormData();
      formData.append('file', file.file);
      this.uploading = true;

      this.$http({
        method: 'post',
        url: 'upfile',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(res => {
        this.uploading = false;
        if (res.code === 200) {
          this.setProofImage(res.data, file.content);
          this.$toast(res.msg || '上传成功');
        } else {
          this.proofImage = '';
          this.proofFileList = [];
          this.$toast(res.msg || '上传失败');
        }
      }).catch(() => {
        this.uploading = false;
        this.proofImage = '';
        this.proofFileList = [];
        this.$toast.fail('上传失败，请重试');
      });
    },
    removeProof() {
      this.proofImage = '';
      this.proofFileList = [];
    },
    getRechargePayload(amount) {
      const method = this.currentMethod;
      const payload = {
        recharge_config_id: method.id,
        pay_way: method.type,
        pay_title: method.title,
        money: amount,
        proof_img: this.proofImage,
        min_amount: method.min,
        max_amount: method.max
      };

      if (method.type === 'bank') {
        payload.bank_name = method.bankName;
        payload.bank_card = method.bankCard;
        payload.real_name = method.realName;
      } else if (method.type === 'usdt') {
        payload.usdt_rate = method.usdtRate;
        payload.usdt_amount = this.usdtAmount;
        payload.usdt_address = method.address;
        payload.qrcode = method.qr;
      } else {
        payload.qrcode = method.qr;
      }

      return payload;
    },
    validatePayload(payload) {
      const fieldNames = {
        recharge_config_id: '充值方式ID',
        pay_way: '支付类型',
        pay_title: '支付名称',
        money: '充值金额',
        proof_img: '转账截图',
        min_amount: '最低充值金额',
        max_amount: '最高充值金额',
        bank_name: '收款银行',
        bank_card: '银行卡号',
        real_name: '收款姓名',
        usdt_rate: 'USDT汇率',
        usdt_amount: '应付USDT',
        usdt_address: 'USDT地址',
        qrcode: '收款二维码'
      };

      for (const key in payload) {
        if (payload[key] === undefined || payload[key] === null || payload[key] === '' || payload[key] === '--') {
          this.$toast('缺少' + (fieldNames[key] || key));
          return false;
        }
      }

      return true;
    },
    submitRecharge() {
      const amount = Number(this.money);
      if (!this.currentMethod.id) {
        this.$toast('请选择充值方式');
        return;
      }
      if (!amount || amount <= 0) {
        this.$toast('请输入充值金额');
        return;
      }
      if (amount < this.currentMethod.min || amount > this.currentMethod.max) {
        this.$toast('充值金额需在' + this.currentMethod.min + '-' + this.currentMethod.max + '之间');
        return;
      }
      if (!this.proofImage) {
        this.$toast('请上传转账截图');
        return;
      }

      const payload = this.getRechargePayload(amount);
      if (!this.validatePayload(payload)) {
        return;
      }

      this.submitting = true;
      this.$http({
        method: 'post',
        url: 'recharge',
        data: payload
      }).then(res => {
        this.submitting = false;
        if (res.code === 200) {
          this.$toast(res.msg || '提交成功，请等待审核');
          this.money = '';
          this.proofImage = '';
          this.proofFileList = [];
          if (this.showRechargeRecords) {
            this.getRechargeRecords();
          }
        } else {
          this.$toast(res.msg || '提交失败');
        }
      }).catch(() => {
        this.submitting = false;
        this.$toast.fail('提交失败，请重试');
      });
    },
    getUserInfo() {
      this.$http({
        method: 'get',
        url: 'user_info'
      }).then(res => {
        if (res.code === 200) {
          this.balance = res.data.money || 0;
        }
      });
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push({ path: '/Login' });
      return;
    }
    this.balance = this.$route.params.balance || 0;
    this.getUserInfo();
    this.getRechargeConfig();
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
  }
};
</script>

<style lang="less" scoped>
@import "../../assets/css/base.css";

.recharge-page {
  min-height: 100vh;
  background: #29243d;
  color: #fff;
}

.recharge-scroll {
  box-sizing: border-box;
  min-height: calc(100vh - 46px);
  padding: 20px 24px 42px;
  overflow-y: auto;
}

.record-link {
  border: 0;
  background: transparent;
  color: #f3a0df;
  font-size: 24px;
  font-weight: 600;
}

.section {
  margin-top: 22px;
  padding: 22px;
  border-radius: 8px;
  background: #342e4c;
}

.section-title {
  margin-bottom: 18px;
  font-size: 28px;
  font-weight: 700;
}

.money-field {
  min-height: 78px;
  border-radius: 8px;
  overflow: hidden;
}

.money-field::after {
  display: none;
}

.limit-row {
  display: flex;
  justify-content: space-between;
  margin-top: 18px;
  color: #cfc7e6;
  font-size: 26px;
}

.custom-select {
  position: relative;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 92px;
  padding: 14px 18px;
  border: 1px solid rgba(243, 160, 223, .22);
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(125, 92, 255, .14), rgba(210, 95, 180, .12)), #29243d;
  color: #fff;
  text-align: left;
}

.select-current {
  display: flex;
  align-items: center;
  min-width: 0;
}

.select-current-icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  margin-right: 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, .09);
  color: #f3a0df;
}

.select-current-icon .van-icon {
  font-size: 30px;
}

.select-current-text {
  min-width: 0;
}

.select-current-text strong {
  display: block;
  overflow: hidden;
  color: #fff;
  font-size: 30px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-current-text span {
  display: block;
  margin-top: 6px;
  color: #cfc7e6;
  font-size: 21px;
}

.select-right {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
  margin-left: 14px;
  color: #f3a0df;
}

.select-right span {
  font-size: 22px;
}

.select-right .van-icon {
  color: #f3a0df;
  font-size: 30px;
  transition: transform .2s ease;
}

.custom-select.open .select-right .van-icon {
  transform: rotate(180deg);
}

.select-options {
  margin-top: 12px;
  padding: 10px;
  border: 1px solid rgba(243, 160, 223, .28);
  border-radius: 8px;
  background: #29243d;
  box-shadow: 0 16px 36px rgba(0, 0, 0, .28);
}

.select-option {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 82px;
  padding: 12px 14px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #fff;
  text-align: left;
}

.select-option + .select-option {
  margin-top: 6px;
}

.select-option.active {
  background: linear-gradient(135deg, rgba(125, 92, 255, .32), rgba(210, 95, 180, .32));
}

.option-icon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  margin-right: 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, .08);
  color: #f3a0df;
}

.option-icon .van-icon {
  font-size: 28px;
}

.option-main {
  flex: 1;
  min-width: 0;
}

.option-main strong {
  display: block;
  overflow: hidden;
  font-size: 26px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-main span {
  display: block;
  margin-top: 6px;
  color: #cfc7e6;
  font-size: 20px;
}

.option-check {
  flex: 0 0 auto;
  margin-left: 10px;
  color: #f3a0df;
  font-size: 26px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 58px;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
  font-size: 24px;
}

.info-row span {
  flex: 0 0 136px;
  color: #cfc7e6;
}

.info-row strong {
  flex: 1;
  color: #fff;
  word-break: break-all;
}

.copy-btn {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 92px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(243, 160, 223, .65);
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(125, 92, 255, .32), rgba(210, 95, 180, .32));
  color: #fff;
  font-size: 22px;
  line-height: 1;
}

.copy-btn .van-icon {
  font-size: 24px;
}

.copy-btn:active {
  transform: scale(.97);
  opacity: .88;
}

.info-tip {
  margin-top: 18px;
  color: #f0c7df;
  font-size: 22px;
  line-height: 1.5;
}

.qr-box {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.qr-box img {
  width: 240px;
  height: 240px;
  border-radius: 8px;
  background: #fff;
  object-fit: cover;
}

.address-box {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding: 16px;
  border-radius: 8px;
  background: #29243d;
}

.address-box span {
  flex: 1;
  color: #fff;
  font-size: 22px;
  line-height: 1.4;
  word-break: break-all;
}

.usdt-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 14px;
}

.usdt-summary div {
  padding: 16px;
  border-radius: 8px;
  background: #29243d;
}

.usdt-summary span {
  display: block;
  margin-bottom: 8px;
  color: #cfc7e6;
  font-size: 22px;
}

.usdt-summary strong {
  display: block;
  color: #fff;
  font-size: 30px;
  word-break: break-all;
}

.address-copy {
  min-width: 128px;
  height: 48px;
}

.upload-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 220px;
  height: 160px;
  border: 1px dashed #80769d;
  border-radius: 8px;
  color: #fff;
  background: #29243d;
}

.upload-box .van-icon {
  margin-bottom: 12px;
  font-size: 42px;
}

.upload-box span {
  font-size: 24px;
}

.submit-btn {
  height: 86px;
  margin-top: 28px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #7d5cff, #d25fb4);
  font-size: 30px;
}

.record-popup {
  overflow: hidden;
  background: #29243d;
}

.record-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: #fff;
}

.record-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 28px 28px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
}

.record-header strong {
  font-size: 30px;
}

.record-header .van-icon {
  color: #cfc7e6;
  font-size: 30px;
}

.record-loading {
  margin-top: 80px;
}

.record-list {
  flex: 1;
  padding: 20px 24px 34px;
  overflow-y: auto;
}

.record-item {
  padding: 20px;
  border-radius: 8px;
  background: #342e4c;
}

.record-item + .record-item {
  margin-top: 16px;
}

.record-item-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.record-item-top strong {
  display: block;
  color: #fff;
  font-size: 32px;
  line-height: 38px;
}

.record-item-top span {
  display: block;
  margin-top: 6px;
  color: #cfc7e6;
  font-size: 22px;
}

.record-status {
  flex: 0 0 auto;
  min-width: 96px;
  padding: 8px 12px;
  border-radius: 999px;
  font-style: normal;
  font-size: 22px;
  line-height: 24px;
  text-align: center;
}

.status-0 {
  color: #d7d0ea;
  background: rgba(215, 208, 234, .14);
}

.status-1 {
  color: #ffd36e;
  background: rgba(255, 211, 110, .14);
}

.status-2 {
  color: #70e0a0;
  background: rgba(112, 224, 160, .14);
}

.status-3 {
  color: #ff8ba0;
  background: rgba(255, 139, 160, .14);
}

.record-row {
  display: flex;
  gap: 12px;
  color: #cfc7e6;
  font-size: 22px;
  line-height: 32px;
}

.record-row + .record-row {
  margin-top: 8px;
}

.record-row span {
  flex: 0 0 104px;
}

.record-row p {
  flex: 1;
  margin: 0;
  color: #fff;
  word-break: break-all;
}

::v-deep .nav-bar {
  background: #29243d;
}

::v-deep .record-popup .van-empty__description {
  color: #cfc7e6;
}

::v-deep .van-nav-bar__title {
  color: #fff;
}

::v-deep .van-cell {
  background: #29243d;
  color: #fff;
}

::v-deep .van-field__label,
::v-deep .van-field__control {
  color: #fff;
}

::v-deep .money-field .van-field__label {
  display: flex;
  align-items: center;
  font-size: 26px;
}

::v-deep .money-field .van-field__control {
  min-height: 54px;
  font-size: 30px;
}

::v-deep .money-field.van-cell::after {
  display: none;
}


::v-deep .van-field__control::placeholder {
  color: #8f86a8;
  font-size: 22px;
}

::v-deep .van-uploader__preview-image,
::v-deep .van-uploader__file {
  width: 220px;
  height: 160px;
  border-radius: 8px;
}

::v-deep .van-uploader__preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
